/**
 * Multi-Month range Date-Picker React component for OBS Search.
 *
 * NOTES:
 * - Date-picker styles can be customized using `./datepicker.css` file.
 */

import React, { useState, useEffect, Fragment } from "react"
import { DateRangePicker, FocusedInputShape } from "react-dates"
import moment, { Moment } from "moment"

import "react-dates/initialize"
import CalendarDatesInfo from "./calendar_dates_info"

/** Date Picket component */
const DatePicker = (props) => {
    // localisation
    const translations = props.translations

    // States initialization
    const minDate = moment()
    const maxDate = minDate.add(2, 'years')

    const [ initialArrivalDate, initialDepartureDate ] = correctDates(props.arrival_date, props.departure_date)
    const [ startDate, setStartDate ] = useState(initialArrivalDate)
    const [ endDate, setEndDate ] = useState(initialDepartureDate)
    const { setOvernightStaysCaption } = props

    const [ focusedInput, setFocusedInput ] = useState(null)

    // https://reactjs.org/docs/hooks-effect.html
    // Dispatch other component state updates every time it gets updated
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    })
    // Dispatch other component state updates once the component is fully rendered
    // https://www.pluralsight.com/guides/event-listeners-in-react-components
    useEffect(() => {
        updateSearchFormFields(startDate, endDate)
        window.addEventListener('change_dates', handleChangeDates);

        // cleanup this component
        return () => {
            window.removeEventListener('change_dates', handleChangeDates);
        };
    }, []);

    const handleChangeDates = (event) => {       
        let newStartDate = moment(document.getElementById("arrival_date").value, "YYYY-MM-DD")
        let newEndDate = moment(document.getElementById("departure_date").value, "YYYY-MM-DD")

        if (event.detail.arrivalDate) {
            newStartDate = moment(event.detail.arrivalDate, "YYYY-MM-DD")

            const ele = document.getElementById("arrivalDateSelector")
            if (ele.classList.contains("animationEven")) {
                ele.classList.remove("animationEven")
                ele.classList.add("animationOdd")
            } else {
                ele.classList.remove("animationOdd")
                ele.classList.add("animationEven")
            }
            
        }
        if (event.detail.departurDate) {
            newEndDate = moment(event.detail.departurDate, "YYYY-MM-DD")

            const ele = document.getElementById("depatureDateSelector")
            if (ele.classList.contains("animationEven")) {
                ele.classList.remove("animationEven")
                ele.classList.add("animationOdd")
            } else {
                ele.classList.remove("animationOdd")
                ele.classList.add("animationEven")
            }
        }
        
        [newStartDate, newEndDate] = correctDates(newStartDate, newEndDate)

        setStartDate(newStartDate)
        setEndDate(newEndDate)

        updateSearchFormFields(newStartDate, newEndDate)
    };

    /** Event when input dates change */
    const onDatesChange = ({ startDate, endDate }) => {
        [startDate, endDate] = correctDates(startDate, endDate)

        setStartDate(startDate)
        setEndDate(endDate)

        updateSearchFormFields(startDate, endDate)
    }

    /** Event when input date focus change */
    const onFocusChange = (focus) => setFocusedInput(focus)

    /** Check for valid arrival weekdays */
    const isStartDayBlocked = (day) => {
        const minimumArrivalDate = props.restrictions.minimum_arrival_date
        if (minimumArrivalDate.isAfter(day, 'day')) return true

        const weekDayRestrictionDate = props.restrictions.weekday_restrictions_application_date
        const weekDayRestriction = props.restrictions.allowed_arrival_weekdays
        if (weekDayRestrictionDate && day.isSameOrAfter(weekDayRestrictionDate, 'day')) {
            if (weekDayRestriction.indexOf(day.day()) === -1) return true
        }

        return false
    }

    /** Check for valid departure weekdays and minlos */
    const isEndDayBlocked = (day) => {
        const minimumArrivalDate = props.restrictions.minimum_arrival_date
        if (minimumArrivalDate.isAfter(day, 'day')) return true

        const weekDayRestrictionDate = props.restrictions.weekday_restrictions_application_date
        if (weekDayRestrictionDate && day.isSameOrAfter(weekDayRestrictionDate, 'day')) {
            const weekDayRestriction = props.restrictions.allowed_departure_weekdays
            if (weekDayRestriction.indexOf(day.day()) === -1) return true
        }

        const minlosRestrictionDate = props.restrictions.minlos_restrictions_application_date
        let minlos = 1
        if (minlosRestrictionDate && startDate.isSameOrAfter(minlosRestrictionDate, 'day')) {   
            minlos = props.restrictions.minlos
        }

        const nextMinlosDay = moment(startDate).add(minlos, 'days')
        return day.isBetween(startDate, nextMinlosDay, 'day')
    }

    /** Depending of the focused input, the events for start or end date will be fired */
    const isDayBlocked =
        focusedInput === 'startDate'
            ? isStartDayBlocked
            : isEndDayBlocked

    /** Reset date-picker input dates to their default state */
    const onResetDatePickerDates = () => {
        setStartDate(initialArrivalDate)
        setEndDate(initialDepartureDate)
        setFocusedInput('startDate')
    }

    /** Close date-picker's popup */
    const onCloseDatePicker = () => {
        setFocusedInput(null)
    }

    /** Calendar information component for datepicker popup */
    const CalendarInfo = () => {
        const selectedDate = focusedInput === 'startDate'
            ? translations.selectArrivalDate
            : translations.selectDepartureDate

        return (
            <Fragment>
                <CalendarDatesInfo
                    selectedDate={selectedDate}
                    buttonReset={translations.buttonReset}
                    buttonDone={translations.buttonDone}
                    onCloseDatePicker={onCloseDatePicker}
                    onResetDatePickerDates={onResetDatePickerDates}
                />
            </Fragment>
        )
    }

    /** Displayed number of months depending on the resolution */
    const numberOfMonths = (window.innerWidth < 620 ? 1 : 2)

    /** Check if the selected startDate and endDate are valid and correct them if needed */
    function correctDates(startDate, endDate) {
        /* Check minimum arrival date */
        const minimumArrivalDate = props.restrictions.minimum_arrival_date
        if (minimumArrivalDate.isAfter(startDate, 'day')) {
            startDate = moment(minimumArrivalDate)
        }

        const weekDayRestrictionDate = props.restrictions.weekday_restrictions_application_date

        /* Check weekday for startDate */
        const arrivalWeekDayRestriction = props.restrictions.allowed_arrival_weekdays
        if (weekDayRestrictionDate && arrivalWeekDayRestriction.length > 0) {
            if (startDate.isSameOrAfter(weekDayRestrictionDate, 'day')) {
                while (arrivalWeekDayRestriction.indexOf(startDate.day()) === -1) {
                    startDate = moment(startDate).add(1, 'days')
                }
            }
        }

        /** Set end date if empty */
        if (!endDate) endDate = moment(startDate).add(1, 'days')

        /* Minlos */
        const minlosRestrictionDate = props.restrictions.minlos_restrictions_application_date
        let minlos = 1
        if (minlosRestrictionDate && startDate.isSameOrAfter(minlosRestrictionDate, 'day')) {   
            minlos = props.restrictions.minlos
        }

        const nextMinlosDay = moment(startDate).add(minlos, 'days')
        if (endDate.isBefore(nextMinlosDay, 'day')) {
            endDate = moment(startDate).add(minlos, 'days')
        }

        /* check weekday for endDate */
        const departureWeekDayRestriction = props.restrictions.allowed_departure_weekdays
        if (weekDayRestrictionDate && departureWeekDayRestriction.length > 0) {
            if (endDate.isSameOrAfter(weekDayRestrictionDate, 'day')) {
                while (departureWeekDayRestriction.indexOf(endDate.day()) === -1) {
                    endDate = moment(endDate).add(1, 'days')
                }
            }
        }

        return [startDate, endDate]
    }

    /** Update search form fields used to search bookings */
    function updateSearchFormFields(startDate, endDate) {
        const duration = moment.duration(endDate.diff(startDate))
        const days = Math.round(duration.asDays())

        const overnightDays = days === 1
            ? translations.staysSingular
            : translations.staysPlural.replace('{count}', days)
        setOvernightStaysCaption(overnightDays)

        // TODO: This needs to be updated to use React state updates instead of plain JS or another framework.
        const eleArrival = document.getElementById("arrival_date")
        if (eleArrival) eleArrival.value = startDate.format('YYYY-MM-DD')

        const eleDeparture = document.getElementById("departure_date")
        if (eleDeparture) eleDeparture.value = endDate.format('YYYY-MM-DD')

        /* 
            The message "arrival date changed" is sent to the alternative calendar.
            If the alternative-calendar is in status "departureDate" then it adds the following css classes:
            - display the restrictions for the minimum booking period 
            - and display the selected arrival date
            - and "calendar is in departure date state"
            If the alternative-calendar is in status "arrivalDate" then it deletes the following css classes:
            -  "minimum booking duration" 
            - and "selected arrival date" 
            - and "calendar is in arrivalDate state" 
        */
        const event = new CustomEvent("changed_selected_dates", {detail: { 
            arrivalDate: startDate.format('YYYY-MM-DD'),
            minlosApplicationDate: (props.restrictions.minlos_restrictions_application_date ? props.restrictions.minlos_restrictions_application_date.format('YYYY-MM-DD') : undefined)
        }});
		window.dispatchEvent(event);
    }

    return (
        <div className="search-bookings">
            <DateRangePicker
                startDate={startDate}
                startDateId="arrivalDateSelector"
                endDate={endDate}
                endDateId="depatureDateSelector"
                minDate={minDate}
                maxDate={maxDate}
                onDatesChange={onDatesChange}
                focusedInput={focusedInput}
                onFocusChange={onFocusChange}
                numberOfMonths={numberOfMonths}
                orientation="horizontal"
                readOnly={true}
                showClearDates={false}
                noBorder={true}
                isDayBlocked={isDayBlocked}
                renderCalendarInfo={CalendarInfo}
                keepOpenOnDateSelect={true}
                reopenPickerOnClearDates={true}
                hideKeyboardShortcutsPanel={true}
            />
        </div>
    )
}

export default DatePicker
